import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Form, Card, Badge } from 'react-bootstrap';
import Select from 'react-select';
import { isEqual } from 'lodash';

const OPERATORS = [
    { value: 'EQUAL_TO', label: '=' },
    { value: 'NOT_EQUAL_TO', label: '!=' },
    { value: 'GREATER_THAN', label: '>' },
    { value: 'LESS_THAN', label: '<' },
    { value: 'GREATER_THAN_EQUAL_TO', label: '>=' },
    { value: 'LESS_THAN_EQUAL_TO', label: '<=' },
    { value: 'INCLUDES', label: '∈' },
    { value: 'NOT_INCLUDES', label: '∉' },
    { value: 'STARTS_WITH', label: '⌘^' },
    { value: 'ENDS_WITH', label: '⌘$' },
    { value: 'CONTAINS', label: '⊃' },
    { value: 'NOT_CONTAINS', label: '⊅' },
    { value: 'AND', label: '&&' },
    { value: 'OR', label: '||' }
];

// Logical operators that don't need field/value
const LOGICAL_OPERATORS = ['AND', 'OR'];

const UpdateConditionForm = ({ formData, setFormData, requestParameters, valueParameters = [], parametersLoading = false }) => {
	console.log('UpdateConditionForm - props:', { formData, requestParameters, valueParameters, parametersLoading });
	
	// multiple row conditions UI; first row is root, others become subFilter
	const [rows, setRows] = useState([{ id: Date.now(), columnName: '', operation: '', baseValue: '', attribute: '' }]);
	const [isInitialized, setIsInitialized] = useState(false);
	const lastFormDataConditionRef = useRef();
	
	console.log('UpdateConditionForm - state:', { rows, isInitialized });

	// Convert condition object to rows format
	const conditionToRows = useCallback((cond) => {
		if (!cond) return [{ id: Date.now(), columnName: '', operation: '', baseValue: '', attribute: '' }];
		
		const toRow = (c, index) => {
			const value = String(c?.columnValue ?? '');
			const parts = value.split('.');
			const base = parts.length >= 2 ? `${parts[0]}.${parts[1]}` : value;
			const attr = parts.length >= 3 ? parts[2] : '';
			return { 
				id: Date.now() + index, 
				columnName: c?.columnName || '', 
				operation: c?.operation || '', 
				baseValue: base, 
				attribute: attr 
			};
		};
		
		const newRows = [toRow(cond, 0)];
		if (Array.isArray(cond.subFilter)) {
			cond.subFilter.forEach((sf, index) => newRows.push(toRow(sf, index + 1)));
		}
		return newRows;
	}, []);

	// Convert rows to condition object
	const rowsToCondition = useCallback((currentRows) => {
		const composeVal = (r) => (r.attribute ? `${r.baseValue}.${r.attribute}` : r.baseValue);
		
		console.log('rowsToCondition - currentRows:', currentRows);
		currentRows.forEach((row, index) => {
			console.log(`Row ${index}:`, {
				columnName: row.columnName,
				operation: row.operation,
				baseValue: row.baseValue,
				attribute: row.attribute
			});
		});
		
		const valid = currentRows.filter(r => r.columnName && r.operation && r.baseValue);
		console.log('rowsToCondition - valid rows:', valid);
		
		if (valid.length === 0) {
			console.log('rowsToCondition - No valid rows, returning undefined');
			return undefined;
		}
		
		const root = { 
			columnName: valid[0].columnName, 
			columnValue: composeVal(valid[0]), 
			operation: valid[0].operation, 
			subFilter: [] 
		};
		root.subFilter = valid.slice(1).map(r => ({ 
			columnName: r.columnName, 
			columnValue: composeVal(r), 
			operation: r.operation, 
			subFilter: [] 
		}));
		
		console.log('rowsToCondition - final condition:', root);
		return root;
	}, []);

	// Initialize from formData.condition only once
	useEffect(() => {
		console.log('UpdateConditionForm - initialization effect:', { 
			isInitialized, 
			'formData?.condition': formData?.condition,
			'lastFormDataConditionRef.current': lastFormDataConditionRef.current 
		});
		
		if (!isInitialized) {
			console.log('UpdateConditionForm - initializing rows from formData.condition');
			const newRows = conditionToRows(formData?.condition);
			setRows(newRows);
			lastFormDataConditionRef.current = formData?.condition;
			setIsInitialized(true);
		}
	}, [formData?.condition, isInitialized]);

	// Handle external condition updates (when editing existing steps)
	useEffect(() => {
		if (isInitialized && formData?.condition && !isEqual(formData.condition, lastFormDataConditionRef.current)) {
			console.log('UpdateConditionForm - external condition update detected:', formData.condition);
			const newRows = conditionToRows(formData.condition);
			setRows(newRows);
			lastFormDataConditionRef.current = formData.condition;
		}
	}, [formData?.condition, isInitialized]);

	// Update formData when rows change (but avoid infinite loops)
	useEffect(() => {
		if (!isInitialized) return;
		
		const newCondition = rowsToCondition(rows);
		console.log('UpdateConditionForm - rows:', rows);
		console.log('UpdateConditionForm - newCondition:', newCondition);
		
		// Only update if the condition actually changed
		if (!isEqual(newCondition, formData?.condition)) {
			setFormData(prev => ({ ...prev, condition: newCondition }));
			lastFormDataConditionRef.current = newCondition;
			console.log('UpdateConditionForm - updated formData.condition:', newCondition);
		}
	}, [rows, isInitialized, rowsToCondition, formData?.condition, setFormData]);

	// Handle row updates with stable callbacks to prevent flickering
	const updateRow = useCallback((index, field, value) => {
		console.log('UpdateConditionForm - updateRow called:', { index, field, value });
		setRows(prev => {
			const next = [...prev];
			next[index] = { ...next[index], [field]: value };
			// Clear attribute if changing baseValue
			if (field === 'baseValue') {
				next[index].attribute = '';
			}
			console.log('UpdateConditionForm - updated rows:', next);
			return next;
		});
	}, []);

	const addRow = useCallback(() => {
		setRows(prev => [...prev, { id: Date.now(), columnName: '', operation: '', baseValue: '', attribute: '' }]);
	}, []);

	const removeRow = useCallback((index) => {
		setRows(prev => prev.filter((_, i) => i !== index));
	}, []);

	return (
		<Form.Group className="mt-3">
			<Form.Label className="fw-bold">Condition to be checked</Form.Label>
			<div className="condition-builder">
				{rows.map((row, idx) => {
					const param = valueParameters.find(p => p.value === row.baseValue) || null;
					const attrOptions = (param && Array.isArray(param.attributes)) 
						? param.attributes.map(a => ({ value: a.name, label: a.name })) 
						: [];
					
					const isSubCondition = idx > 0;
					const isLogicalOperator = LOGICAL_OPERATORS.includes(row.operation);
					
					return (
						<Card 
							key={row.id} 
							className={`mb-3 ${isSubCondition ? 'ms-4 border-start border-3 border-secondary' : isLogicalOperator ? 'border-warning' : 'border-primary'}`}
							style={{ 
								backgroundColor: isLogicalOperator ? '#fff8e1' : isSubCondition ? '#f8f9fa' : '#ffffff',
								borderRadius: '8px'
							}}
						>
							<Card.Body className="p-3">
								{isLogicalOperator && (
									<div className="mb-2">
										<Badge bg="warning" text="dark" className="mb-2">
											<i className="fas fa-code-branch me-1"></i>
											Logical Operator
										</Badge>
									</div>
								)}
								{isSubCondition && !isLogicalOperator && (
									<div className="mb-2">
										<Badge bg="secondary" className="mb-2">
											<i className="fas fa-link me-1"></i>
											AND Condition {idx}
										</Badge>
									</div>
								)}
								{idx === 0 && !isLogicalOperator && (
									<div className="mb-2">
										<Badge bg="primary" className="mb-2">
											<i className="fas fa-filter me-1"></i>
											Primary Condition
										</Badge>
									</div>
								)}
								
								<div className="row g-2 align-items-end">
									{/* Field Column - Hidden for logical operators */}
									{!isLogicalOperator && (
										<div className="col-md-3">
											{idx === 0 && <Form.Label className="small text-muted fw-semibold">Field</Form.Label>}
											<Select
												options={requestParameters}
												value={requestParameters.find(param => param.value === row.columnName) || null}
												onChange={(selected) => updateRow(idx, 'columnName', selected?.value || '')}
												placeholder="Select Field"
												isDisabled={parametersLoading}
												styles={{
													control: (base) => ({
														...base,
														minHeight: '38px',
														borderColor: isSubCondition ? '#6c757d' : '#0d6efd'
													})
												}}
											/>
										</div>
									)}
									
									{/* Operator Column - Always visible but wider for logical operators */}
									<div className={isLogicalOperator ? "col-md-4" : "col-md-2"}>
										{idx === 0 && <Form.Label className="small text-muted fw-semibold">Operator</Form.Label>}
										<Select
											options={OPERATORS}
											value={OPERATORS.find(op => op.value === row.operation) || null}
											onChange={(selected) => {
												const newOperation = selected?.value || '';
												updateRow(idx, 'operation', newOperation);
												// Clear field and value if switching to logical operator
												if (LOGICAL_OPERATORS.includes(newOperation)) {
													updateRow(idx, 'columnName', '');
													updateRow(idx, 'baseValue', '');
													updateRow(idx, 'attribute', '');
												}
											}}
											placeholder="Select Operator"
											isDisabled={parametersLoading}
											styles={{
												control: (base) => ({
													...base,
													minHeight: '38px',
													borderColor: isLogicalOperator ? '#ffc107' : isSubCondition ? '#6c757d' : '#0d6efd'
												})
											}}
										/>
									</div>
									
									{/* Value Parameter Column - Hidden for logical operators */}
									{!isLogicalOperator && (
										<div className="col-md-3">
											{idx === 0 && <Form.Label className="small text-muted fw-semibold">Value Parameter</Form.Label>}
											<Select
												isClearable
												isLoading={parametersLoading}
												options={valueParameters}
												value={param}
												onChange={(selected) => updateRow(idx, 'baseValue', selected?.value || '')}
												placeholder={parametersLoading ? "Loading..." : "Select parameter"}
												styles={{
													control: (base) => ({
														...base,
														minHeight: '38px',
														borderColor: isSubCondition ? '#6c757d' : '#0d6efd'
													})
												}}
											/>
										</div>
									)}
									
									{/* Value Attribute Column - Hidden for logical operators */}
									{!isLogicalOperator && (
										<div className="col-md-3">
											{idx === 0 && attrOptions.length > 0 && (
												<Form.Label className="small text-muted fw-semibold">Value Attribute</Form.Label>
											)}
											<Select
												isClearable
												options={attrOptions}
												value={row.attribute ? { value: row.attribute, label: row.attribute } : null}
												onChange={(selected) => updateRow(idx, 'attribute', selected?.value || '')}
												placeholder="Select attribute"
												isDisabled={!attrOptions.length || parametersLoading}
												styles={{
													control: (base) => ({
														...base,
														minHeight: '38px',
														borderColor: isSubCondition ? '#6c757d' : '#0d6efd',
														opacity: !attrOptions.length ? 0.6 : 1
													})
												}}
											/>
										</div>
									)}
									
									{/* Actions Column */}
									<div className={isLogicalOperator ? "col-md-8 d-flex justify-content-between align-items-center" : "col-md-1 d-flex justify-content-end"}>
										{isLogicalOperator && (
											<div className="d-flex gap-2">
												<button
													type="button"
													className="btn btn-outline-success btn-sm"
													onClick={() => {
														// Add nested condition after this logical operator
														const newCondition = { id: Date.now(), columnName: '', operation: '', baseValue: '', attribute: '' };
														setRows(prev => {
															const newRows = [...prev];
															newRows.splice(idx + 1, 0, newCondition);
															return newRows;
														});
													}}
													disabled={parametersLoading}
													title="Add nested condition"
												>
													<i className="fas fa-indent me-1"></i>
													Add Nested Condition
												</button>
												<span className="text-muted small align-self-center">
													This operator will group conditions below
												</span>
											</div>
										)}
										
										{idx === 0 && !isLogicalOperator ? (
											<button
												type="button"
												className="btn btn-outline-primary btn-sm"
												onClick={addRow}
												disabled={parametersLoading}
												title="Add condition"
											>
												<i className="fas fa-plus me-1"></i>
												Add
											</button>
										) : (
											<button
												type="button"
												className="btn btn-outline-danger btn-sm"
												onClick={() => removeRow(idx)}
												disabled={parametersLoading}
												title="Remove this condition"
											>
												<i className="fas fa-trash"></i>
											</button>
										)}
									</div>
								</div>
								
								{/* Show condition preview */}
								{isLogicalOperator ? (
									<div className="mt-2 p-2 bg-warning bg-opacity-10 rounded small text-warning-emphasis">
										<i className="fas fa-code-branch me-1"></i>
										<strong>Logical Operator:</strong> {OPERATORS.find(op => op.value === row.operation)?.label} - Groups conditions that follow
									</div>
								) : row.columnName && row.operation && row.baseValue && (
									<div className="mt-2 p-2 bg-light rounded small text-muted">
										<i className="fas fa-eye me-1"></i>
										<strong>Preview:</strong> {row.columnName} {OPERATORS.find(op => op.value === row.operation)?.label} {row.baseValue}{row.attribute ? `.${row.attribute}` : ''}
									</div>
								)}
							</Card.Body>
						</Card>
					);
				})}
				
				{rows.length === 0 && (
					<Card className="text-center p-4 bg-light border-dashed">
						<Card.Body>
							<i className="fas fa-filter fa-2x text-muted mb-2"></i>
							<p className="text-muted mb-2">No conditions defined</p>
							<button
								type="button"
								className="btn btn-primary btn-sm"
								onClick={addRow}
								disabled={parametersLoading}
							>
								<i className="fas fa-plus me-1"></i>
								Add First Condition
							</button>
						</Card.Body>
					</Card>
				)}
				
				{/* Condition Structure Preview */}
				{rows.length > 1 && (
					<Card className="mt-3 bg-info bg-opacity-10 border-info">
						<Card.Body className="p-3">
							<h6 className="text-info mb-2">
								<i className="fas fa-sitemap me-1"></i>
								Condition Structure
							</h6>
							<div className="small text-muted">
								<div className="mb-1">
									<i className="fas fa-circle text-primary me-2"></i>
									<strong>Primary:</strong> {rows[0]?.columnName || 'Not set'} {OPERATORS.find(op => op.value === rows[0]?.operation)?.label || ''} {rows[0]?.baseValue || ''}
								</div>
								{rows.slice(1).map((row, idx) => {
									const isLogical = LOGICAL_OPERATORS.includes(row.operation);
									return (
										<div key={row.id} className="mb-1 ms-3">
											{isLogical ? (
												<>
													<i className="fas fa-code-branch text-warning me-2"></i>
													<strong>LOGIC:</strong> {OPERATORS.find(op => op.value === row.operation)?.label} operator
												</>
											) : (
												<>
													<i className="fas fa-link text-secondary me-2"></i>
													<strong>AND:</strong> {row.columnName || 'Not set'} {OPERATORS.find(op => op.value === row.operation)?.label || ''} {row.baseValue || ''}
												</>
											)}
										</div>
									);
								})}
							</div>
						</Card.Body>
					</Card>
				)}
			</div>
		</Form.Group>
	);
};

export default UpdateConditionForm; 